import React, { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import i18n from "i18next";
import { normalizeAnimationName } from "../../../../../utils/animationHelpers";
import "./index.scss";

const t = (key) => i18n.t(`messages.animationPanel.${key}`);

function PositionPicker({ x, y, onChange }) {
  const ref = useRef(null);

  const compute = useCallback(
    (e) => {
      const rect = ref.current.getBoundingClientRect();
      const nx = Math.round(
        Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100))
      );
      const ny = Math.round(
        Math.min(100, Math.max(0, ((e.clientY - rect.top) / rect.height) * 100))
      );
      onChange(nx, ny);
    },
    [onChange]
  );

  const handleMouseDown = useCallback(
    (e) => {
      e.preventDefault();
      compute(e);
      const onMove = (e) => compute(e);
      const onUp = () => {
        document.removeEventListener("mousemove", onMove);
        document.removeEventListener("mouseup", onUp);
      };
      document.addEventListener("mousemove", onMove);
      document.addEventListener("mouseup", onUp);
    },
    [compute]
  );

  return (
    <div className="ap-pos-picker" ref={ref} onMouseDown={handleMouseDown}>
      <div className="ap-pos-h-line" style={{ top: `${y}%` }} />
      <div className="ap-pos-v-line" style={{ left: `${x}%` }} />
      {[
        [0, 0],
        [50, 0],
        [100, 0],
        [0, 50],
        [50, 50],
        [100, 50],
        [0, 100],
        [50, 100],
        [100, 100],
      ].map(([gx, gy]) => (
        <div
          key={`${gx}-${gy}`}
          className="ap-pos-hint"
          style={{ left: `${gx}%`, top: `${gy}%` }}
        />
      ))}
      <div className="ap-pos-dot" style={{ left: `${x}%`, top: `${y}%` }} />
      <div className="ap-pos-coords">
        {x}% · {y}%
      </div>
    </div>
  );
}

const ENTRY_PRESETS = [
  { id: null, name: "Yok" },
  { id: "Pan", name: "Pan Giriş", css: "ap-prev--pan-left", featured: true },
  { id: "Fade", name: "Aydınlatma", css: "ap-prev--fade-in" },
  { id: "Scale In", name: "Yakınlaştır", css: "ap-prev--scale-in" },
  { id: "Slide In Right", name: "Sağa Kaydır", css: "ap-prev--slide-right" },
  { id: "Slide In Left", name: "Soldan Gir", css: "ap-prev--slide-left" },
  { id: "Slide In Up", name: "Yukarı Kaydır", css: "ap-prev--slide-up" },
  { id: "Slide In Down", name: "Aşağı Kaydır", css: "ap-prev--slide-down" },
  { id: "Rotate", name: "Döndür", css: "ap-prev--rotate" },
  { id: "Bounce", name: "Zıpla", css: "ap-prev--bounce" },
  { id: "Bounce In", name: "Bounce In", css: "ap-prev--bounce-in" },
  { id: "Rotate In", name: "Rotate In", css: "ap-prev--rotate-in" },
  { id: "Type Writer", name: "Type Writer", css: "ap-prev--typewriter" },
  { id: "Wavy Text", name: "Wavy Text", css: "ap-prev--wavy" },
  { id: "Flip", name: "Flip", css: "ap-prev--flip" },
  { id: "Pinwheel", name: "Pinwheel", css: "ap-prev--pinwheel" },
  { id: "Roll", name: "Roll", css: "ap-prev--roll" },
  { id: "Stretch", name: "Stretch", css: "ap-prev--stretch" },
];

const EXIT_PRESETS = [
  { id: null, name: "Yok" },
  { id: "Pan Out", name: "Pan Çıkış", css: "ap-prev--pan-out-left", featured: true },
  { id: "Fade Out", name: "Karart", css: "ap-prev--fade-out" },
  { id: "Scale Out", name: "Uzaklaştır", css: "ap-prev--scale-out" },
  { id: "Slide Out Right", name: "Sağa Çık", css: "ap-prev--slide-out-right" },
  { id: "Slide Out Left", name: "Sola Çık", css: "ap-prev--slide-out-left" },
  { id: "Slide Out Up", name: "Yukarı Çık", css: "ap-prev--slide-out-up" },
  { id: "Slide Out Down", name: "Aşağı Çık", css: "ap-prev--slide-out-down" },
  { id: "Rotate Out", name: "Döndür", css: "ap-prev--rotate-out" },
  { id: "Bounce Out", name: "Zıpla", css: "ap-prev--bounce-out" },
  { id: "Type Writer Out", name: "Type Writer", css: "ap-prev--typewriter-out" },
  { id: "Wavy Text Out", name: "Wavy Text", css: "ap-prev--wavy-out" },
];

const TEXT_ONLY_ANIMATIONS = new Set(["Type Writer", "Wavy Text"]);

const WAVY_THUMBS = new Set(["Wavy Text", "Wavy Text Out"]);

// Yön artık ayrı preset isimleri yerine PositionPicker ile (panOriginX/Y) seçiliyor —
// tek "Pan" (giriş) / "Pan Out" (çıkış) presetimiz var.
const PAN_NAMES = new Set(["Pan", "Pan Out"]);

const PAN_NAME_KEYS = { Pan: "panEntryName", "Pan Out": "panExitName" };

const ALL_PRESETS = [...ENTRY_PRESETS, ...EXIT_PRESETS];

export function animationDisplayName(id) {
  if (PAN_NAME_KEYS[id]) return t(PAN_NAME_KEYS[id]);
  const key = `messages.animationNames.${id}`;
  const label = i18n.t(key);
  if (label && label !== key) return label;
  return ALL_PRESETS.find((p) => p.id === id)?.name || id;
}

const PAN_DEFAULT_ORIGINS = {
  Pan: { x: 100, y: 50 },
  "Pan Out": { x: 0, y: 50 },
};

const DEFAULT_PAN_SCALE = 1.15;

export default function AnimationPanel({
  box,
  elementType = "element",
  onApplyEntry,
  onApplyExit,
  onClose,
  hideHeader = false,
  // Field-mode: when fieldLabel is set the panel reads entry/exit from the
  // explicit props rather than from box.boxAnimation. Caller routes
  // onApplyEntry/onApplyExit to the correct field write path.
  entryAnimation = null,
  exitAnimation = null,
  fieldLabel = null,
  syncKey = null,
  panControlsHost = null,
  panSettingsCollapsed = false,
  onPanStateChange = null,
}) {
  const [tab, setTab] = useState("in");

  // In field-mode never fall back to the parent box's animation — otherwise
  // every unset sub-field would appear to inherit the parent's preset.
  const isFieldMode = fieldLabel != null;
  const entrySource = isFieldMode ? entryAnimation : (entryAnimation ?? box?.boxAnimation ?? null);
  const exitSource = isFieldMode ? exitAnimation : (exitAnimation ?? box?.boxExitAnimation ?? null);

  const _entryRaw =
    typeof entrySource?.animation === "string"
      ? entrySource.animation
      : (entrySource?.animation?.name ?? null);
  const _exitRaw =
    typeof exitSource?.animation === "string"
      ? exitSource.animation
      : (exitSource?.animation?.name ?? null);
  const currentEntry = normalizeAnimationName(_entryRaw);
  const currentExit = normalizeAnimationName(_exitRaw);

  const [entryDuration, setEntryDuration] = useState(
    typeof entrySource?.duration === "number" ? entrySource.duration : 1
  );
  const [exitDuration, setExitDuration] = useState(
    typeof exitSource?.duration === "number" ? exitSource.duration : 1
  );

  const [panScale, setPanScale] = useState(DEFAULT_PAN_SCALE);
  const [panOriginX, setPanOriginX] = useState(50);
  const [panOriginY, setPanOriginY] = useState(50);
  // "out" (varsayılan, geriye dönük uyumlu): kutu zoomScale'den 1'e küçülür (uzaklaşır)
  // "in": kutu 1'den zoomScale'e büyür (yakınlaşır) — giriş ve çıkışta bağımsız seçilebilir
  const [panDirection, setPanDirection] = useState("out");

  // Sync state when selected box/field or tab changes
  useEffect(() => {
    setEntryDuration(typeof entrySource?.duration === "number" ? entrySource.duration : 1);
    setExitDuration(typeof exitSource?.duration === "number" ? exitSource.duration : 1);

    const animName =
      tab === "in" ? normalizeAnimationName(_entryRaw) : normalizeAnimationName(_exitRaw);
    const animObj =
      tab === "in"
        ? typeof entrySource?.animation === "object"
          ? entrySource.animation
          : null
        : typeof exitSource?.animation === "object"
          ? exitSource.animation
          : null;

    if (animName && PAN_NAMES.has(animName)) {
      const defaults = PAN_DEFAULT_ORIGINS[animName] ?? { x: 50, y: 50 };
      setPanScale(animObj?.panScale ?? DEFAULT_PAN_SCALE);
      setPanOriginX(animObj?.panOriginX ?? defaults.x);
      setPanOriginY(animObj?.panOriginY ?? defaults.y);
      setPanDirection(animObj?.panDirection ?? "out");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [syncKey ?? box?.id, tab]);

  const isText = elementType === "text";
  const presets = (tab === "in" ? ENTRY_PRESETS : EXIT_PRESETS).filter(
    (p) => p.id === null || isText || !TEXT_ONLY_ANIMATIONS.has(p.id)
  );
  const current = tab === "in" ? currentEntry : currentExit;
  const duration = tab === "in" ? entryDuration : exitDuration;
  const isPan = !!(current && PAN_NAMES.has(current));

  useEffect(() => {
    onPanStateChange?.({ isPan });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPan]);

  const timelineDuration = box?.timeline
    ? Math.max(
        1,
        Math.round(((box.timeline.endFrame ?? 1800) - (box.timeline.startFrame ?? 0)) / 30)
      )
    : 60;
  const durationMax = isPan ? timelineDuration : 3;
  const durationInPanCard = isPan && !panSettingsCollapsed;

  const _dispatch = (animName, dur, ox, oy, ps, pd) => {
    const panOptions =
      animName && PAN_NAMES.has(animName)
        ? { panScale: ps, panOriginX: ox, panOriginY: oy, panDirection: pd }
        : undefined;
    if (tab === "in") {
      onApplyEntry(animName, dur, panOptions);
    } else {
      onApplyExit(animName, dur, panOptions);
    }
  };

  const handleSelect = (preset) => {
    if (preset.id && PAN_NAMES.has(preset.id)) {
      const defaults = PAN_DEFAULT_ORIGINS[preset.id] ?? { x: 50, y: 50 };
      setPanOriginX(defaults.x);
      setPanOriginY(defaults.y);
      _dispatch(
        preset.id,
        tab === "in" ? entryDuration : exitDuration,
        defaults.x,
        defaults.y,
        panScale,
        panDirection
      );
      onPanStateChange?.({ isPan: true, reopen: true });
    } else if (tab === "in") {
      onApplyEntry(preset.id, entryDuration);
    } else {
      onApplyExit(preset.id, exitDuration);
    }
  };

  const handleDurationChange = (val) => {
    if (tab === "in") {
      setEntryDuration(val);
      if (currentEntry)
        _dispatch(currentEntry, val, panOriginX, panOriginY, panScale, panDirection);
    } else {
      setExitDuration(val);
      if (currentExit) _dispatch(currentExit, val, panOriginX, panOriginY, panScale, panDirection);
    }
  };

  const handlePanScaleChange = (val) => {
    setPanScale(val);
    if (current) _dispatch(current, duration, panOriginX, panOriginY, val, panDirection);
  };

  const handleOriginChange = (x, y) => {
    setPanOriginX(x);
    setPanOriginY(y);
    if (current) _dispatch(current, duration, x, y, panScale, panDirection);
  };

  const handlePanDirectionChange = (dir) => {
    setPanDirection(dir);
    if (current) _dispatch(current, duration, panOriginX, panOriginY, panScale, dir);
  };

  const durationRow = (
    <div className="ap-duration-row">
      <span className="ap-duration-value">{duration.toFixed(1)}s</span>
      <input
        type="range"
        min="0.1"
        max={durationMax}
        step="0.1"
        value={duration}
        className="ap-slider"
        onChange={(e) => handleDurationChange(parseFloat(e.target.value))}
      />
    </div>
  );

  return (
    <div className="ap-panel ap-panel--flow">
      {!hideHeader && (
        <div className="ap-header">
          <span className="ap-title">Animasyon</span>
          <button className="ap-close" onClick={onClose}>
            <i className="material-icons" style={{ fontSize: "20px" }}>
              close
            </i>
          </button>
        </div>
      )}

      <div className="ap-tabs">
        <button
          className={`ap-tab ${tab === "in" ? "ap-tab--active" : ""}`}
          onClick={() => setTab("in")}
        >
          {i18n.t("AnimationEntrance") || "Giriş"}
        </button>
        <button
          className={`ap-tab ${tab === "out" ? "ap-tab--active" : ""}`}
          onClick={() => setTab("out")}
        >
          {i18n.t("AnimationExit") || "Çıkış"}
        </button>
      </div>

      <div className="ap-grid">
        {presets.map((preset) => (
          <div
            key={preset.id ?? "__none__"}
            className={`ap-card ${current === preset.id ? "ap-card--selected" : ""}`}
            onClick={() => handleSelect(preset)}
          >
            {preset.id === null ? (
              <div className="ap-card-thumb ap-card-none">
                <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="20" cy="20" r="15" stroke="#6b7280" strokeWidth="2" />
                  <line x1="9" y1="9" x2="31" y2="31" stroke="#6b7280" strokeWidth="2" />
                </svg>
              </div>
            ) : (
              <div className={`ap-card-thumb ${preset.css}`}>
                {preset.featured && <span className="ap-card-star">★</span>}
                {WAVY_THUMBS.has(preset.id) ? (
                  // Per-letter spans so the thumbnail shows an actual wave
                  // (staggered delays in index.scss), not a whole-word fade.
                  <span className="ap-card-demo ap-card-demo--wavy">
                    {"ABC".split("").map((ch, i) => (
                      <span key={i} style={{ animationDelay: `${i * 0.12}s` }}>
                        {ch}
                      </span>
                    ))}
                  </span>
                ) : (
                  <span className="ap-card-demo">ABC</span>
                )}
              </div>
            )}
            <span className="ap-card-name">
              {preset.id ? animationDisplayName(preset.id) : preset.name}
            </span>
          </div>
        ))}
      </div>

      {!durationInPanCard && (
        <div className="ap-footer">
          <p className="ap-footer-label">Hareketli ve hareketsiz süre</p>
          {durationRow}
        </div>
      )}

      {isPan &&
        !panSettingsCollapsed &&
        (() => {
          const panControls = (
            <div className="ap-pan-controls">
              <div className="ap-pan-section">
                <span className="ap-pan-label">Hareketli ve hareketsiz süre</span>
                {durationRow}
              </div>

              <div className="ap-pan-section">
                <span className="ap-pan-label">{t("direction")}</span>
                <div className="ap-pan-dir">
                  <button
                    className={`ap-pan-dir-btn ${panDirection === "out" ? "ap-pan-dir-btn--active" : ""}`}
                    onClick={() => handlePanDirectionChange("out")}
                  >
                    {t("zoomOut")}
                  </button>
                  <button
                    className={`ap-pan-dir-btn ${panDirection === "in" ? "ap-pan-dir-btn--active" : ""}`}
                    onClick={() => handlePanDirectionChange("in")}
                  >
                    {t("zoomIn")}
                  </button>
                </div>
              </div>

              <div className="ap-pan-section">
                <span className="ap-pan-label">{t("zoomAmount")}</span>
                <div className="ap-pan-row">
                  <input
                    type="range"
                    min="1.0"
                    max="10"
                    step="0.05"
                    value={panScale}
                    className="ap-slider"
                    onChange={(e) => handlePanScaleChange(parseFloat(e.target.value))}
                  />
                  <span className="ap-pan-zoom-value">{panScale.toFixed(2)}×</span>
                </div>
              </div>

              <div className="ap-pan-section">
                <span className="ap-pan-label">{t("startPoint")}</span>
                <PositionPicker x={panOriginX} y={panOriginY} onChange={handleOriginChange} />
                <div className="ap-pan-xy-row">
                  <label className="ap-pan-xy-label">
                    X
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={panOriginX}
                      className="ap-pan-xy-input"
                      onChange={(e) => {
                        const v = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                        handleOriginChange(v, panOriginY);
                      }}
                    />
                    <span className="ap-pan-xy-unit">%</span>
                  </label>
                  <label className="ap-pan-xy-label">
                    Y
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={panOriginY}
                      className="ap-pan-xy-input"
                      onChange={(e) => {
                        const v = Math.min(100, Math.max(0, parseInt(e.target.value) || 0));
                        handleOriginChange(panOriginX, v);
                      }}
                    />
                    <span className="ap-pan-xy-unit">%</span>
                  </label>
                </div>
              </div>
            </div>
          );
          return panControlsHost ? createPortal(panControls, panControlsHost) : panControls;
        })()}
    </div>
  );
}
